import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import useThrottle from 'src/hooks/useThrottle';
// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';
import { IBlock } from 'src/@types/Choqorak/Settings/Block';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useStore } from 'src/stores/store';

// ----------------------------------------------------------------------

export default observer(function BlockNewEditForm() {
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };
  const { BlockStore, ChoqorakCommonDropdown } = useStore();
  const { translate } = useLocales();
  const { createBlock, updateBlock, editMode, selectedBlock, clearSelectedBlock } = BlockStore;
  const [floorIDs, setFloorIDs] = useState<any>([]);
  const { FloorOption, loadFloorDDL } = ChoqorakCommonDropdown;
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const NewBlockSchema = Yup.object().shape({
    blockName: Yup.string().required(`${translate('Validation.EnglishName')}`),
    blockAddress: Yup.string().required(`${translate('Validation.DariName')}`),
  });
  const floor = selectedBlock?.floorIds!.map((m) => m);
  const defaultValues = useMemo<IBlock>(
    () => ({
      blockId: selectedBlock?.blockId,
      blockName: selectedBlock?.blockName || '',
      blockAddress: selectedBlock?.blockAddress || '',
      FloorIds: selectedBlock?.floorIds || undefined,
    }),
    [selectedBlock]
  );

  const methods = useForm<IBlock>({
    resolver: yupResolver(NewBlockSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setValue,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = useThrottle(  (data: IBlock) => {
    if (data.blockId! === undefined) {
      ///create
      createBlock(data)
        .then(() => {
          reset();
          enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
          navigate(PATH_DASHBOARD.Block.list);
        })
        .catch((err) => {
          var json = JSON.parse(err.request.response);
          if (json.error != null) {
            setError('afterSubmit', { ...err, message: json.message });
          }
        });
    } else {
      ///update
      updateBlock(data)
        .then(() => {
          reset();
          enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
          navigate(PATH_DASHBOARD.Block.list);
        })
        .catch((err) => {
          var json = JSON.parse(err.request.response);
          if (json.error != null) {
            setError('afterSubmit', { ...err, message: json.message });
          }
        });
    }
  });

  useEffect(() => {
    loadFloorDDL();
    if (editMode) {
      setFloorIDs(floor);
      reset(defaultValues);
    }
    if (!editMode) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, editMode, defaultValues]);

  const floorChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;

    setFloorIDs(typeof value === 'string' ? value.split(',') : value);
    setValue<any>('floorIds', typeof value === 'string' ? value.split(',') : value);
  };
  console.log(floorIDs);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField
                name="blockName"
                label={translate('Block.BlockName')}
                showAsterisk={true}
                autoFocus
              />
              <RHFTextField
                name="blockAddress"
                label={translate('Block.BlockAddress')}
                showAsterisk={true}
              />
              <FormControl>
                <InputLabel id="demo-multiple-name-label">{translate('Block.Floor')}</InputLabel>
                <Select
                  labelId="demo-multiple-name-label"
                  id="demo-multiple-name"
                  name="floorIds"
                  multiple
                  value={floorIDs}
                  onChange={floorChange}
                  input={<OutlinedInput label="Floors" />}
                  MenuProps={MenuProps}
                >
                  {FloorOption.map((floor) => (
                    <MenuItem key={floor.value} value={floor.value}>
                      {floor.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={
                  !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                }
              >
                {!editMode ? `${translate('CRUD.Save')}` : `${translate('CRUD.Update')}`}
              </LoadingButton>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  clearSelectedBlock();
                  navigate(PATH_DASHBOARD.Block.list);
                }}
              >
                {translate('CRUD.BackToList')}
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
